import {
  AfterContentInit,
  ComponentRef,
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  input,
  NgZone,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgControl } from '@angular/forms';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { ViewportService } from '../../services/viewport.service';
import {
  BottomSheetOption,
  MatSelectBottomSheetContentComponent,
} from './mat-select-bottom-sheet-content.component';

@Directive({
  selector: 'mat-select:not([noBottomSheet])',
  standalone: true,
})
export class MatSelectBottomSheetDirective implements AfterContentInit {
  private readonly matSelect = inject(MatSelect);
  private readonly viewport = inject(ViewportService);
  private readonly vcr = inject(ViewContainerRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly elRef = inject(ElementRef<HTMLElement>);
  private readonly zone = inject(NgZone);
  private readonly ngControl = inject(NgControl, {
    optional: true,
    self: true,
  });

  readonly sheetTitle = input('COMMON.SELECT');
  readonly sheetSearchEnabled = input(false);

  private contentRef: ComponentRef<MatSelectBottomSheetContentComponent> | null =
    null;

  ngAfterContentInit(): void {
    // Intercept click before mat-select opens its CDK panel
    const clickHandler = (e: Event): void => {
      if (this.viewport.isMenuMobile()) {
        e.stopImmediatePropagation();
        e.preventDefault();
        this.zone.run(() => this.openSheet());
      }
    };

    const el = this.elRef.nativeElement;
    this.zone.runOutsideAngular(() => {
      el.addEventListener('click', clickHandler, true);
    });
    this.destroyRef.onDestroy(() => {
      el.removeEventListener('click', clickHandler, true);
    });

    // Fallback: if the panel still opens (e.g. keyboard), close and redirect
    this.matSelect.openedChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((opened) => {
        if (opened && this.viewport.isMenuMobile()) {
          this.matSelect.close();
          this.openSheet();
        }
      });
  }

  private openSheet(): void {
    const comp = this.getOrCreateContent();
    const inst = comp.instance;

    inst.options.set(this.buildOptions());
    inst.isMultiple.set(this.matSelect.multiple);
    inst.title.set(this.sheetTitle());
    inst.searchEnabled.set(this.sheetSearchEnabled());
    inst.compareWith.set(this.matSelect.compareWith);

    inst.open(this.matSelect.value);
  }

  private buildOptions(): BottomSheetOption[] {
    return this.matSelect.options
      .toArray()
      .filter((opt) => opt.value !== undefined && opt.value !== null)
      .map((opt) => ({
        value: opt.value,
        viewValue: opt.viewValue,
        disabled: opt.disabled,
        group: opt.group?.label ?? null,
      }));
  }

  private getOrCreateContent(): ComponentRef<MatSelectBottomSheetContentComponent> {
    if (this.contentRef) return this.contentRef;

    this.contentRef = this.vcr.createComponent(
      MatSelectBottomSheetContentComponent,
    );

    this.contentRef.instance.applied.subscribe((value: any) => {
      this.applyValue(value);
    });

    this.destroyRef.onDestroy(() => {
      this.contentRef?.destroy();
      this.contentRef = null;
    });

    return this.contentRef;
  }

  private applyValue(value: any): void {
    if (this.ngControl?.control) {
      this.ngControl.control.setValue(value);
    } else {
      this.matSelect.value = value;
    }
    this.matSelect.selectionChange.emit(
      new MatSelectChange(this.matSelect, value),
    );
  }
}
